import React from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useParams } from "react-router-dom";
import { useGetContractManagementCountByStatusQuery } from "store/slices/apis/contractManagementDashboardSlice";
import DashboardPanelHeader from "components/DashboardPanelHeader";


const items = [
  { label: "Approved", count: 9, value: 6.5, color: "#C0CA33" },
  { label: "Pending Approval", count: 6, value: 4.2, color: "#FF9800" },
  { label: "Rejected", count: 4, value: 2.07, color: "#E65F2B" },

];

const colorPlate = ["#C0CA33", "#FF9800", "#E65F2B"]

const ChangeOrderStatus = (props) => {

  const { RaisedType } = props

  const { projectId } = useParams();
  const getRaisedType = (type) => {
    switch (type) {
      case "Claim Order Received":
        return "Change Order Received";

      case "Claim Order Raised":
        return "Change Order Raised";

      default:
        return "";
    }
  };

  const params = {
    projectId: projectId,
    raisedType: getRaisedType(RaisedType)
  }

  const { data, isLoading, isFetching } = useGetContractManagementCountByStatusQuery(params)
  const totalCount = data?.data.totalCount
  const totalValue = data?.data.totalValue
  const optionsForCount = {
    chart: {
      type: "pie",
      height: 220,
      backgroundColor: "transparent",
      margin: 0,
    },

    title: {
      text: `<div style="text-align:center">
                    <span style="font-size:20px;font-weight:400;font-family:Outfit; color:#000000">${totalCount}</span><br/>
                  </div>`,
      align: "center",
      verticalAlign: "middle",
      useHTML: true,
      y: 20,
    },

    tooltip: {
      enabled: true,
      useHTML: true,
      headerFormat: "",
      pointFormat: `
        <div style="font-family: Outfit; font-size: 12px; padding: 4px;">
          <span style="color:{point.color}">●</span> <b>{point.name}</b>: {point.y}
        </div>
      `,
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderRadius: 8,
      shadow: true,
    },

    plotOptions: {
      pie: {
        innerSize: "60%",
        size: "75%",
        borderWidth: 0,
        borderRadius: 0,
        borderColor: "#fff",
        dataLabels: {
          enabled: true,
          useHTML: true,
          distance: 4,
          connectorPadding: 1,
          crop: false,
          overflow: "allow",
          formatter: function () {
            return `
          <div style="
            max-width: 60px;
            white-space: normal;
            word-wrap: break-word;
            text-align: center;
            font-size: 12px;
            font-weight: 600;
            font-family: Outfit, sans-serif;
          ">
            ${this.point.name} (${this.point.y})
          </div>
        `;
          },
        },
      },
    },

    series: [
      {
        name: "Change Order Count",
        data: data?.data?.statusByCount.map((item, ind) => ({
          y: Number(item.count),
          name: item.name,
          color: colorPlate[ind],
        })),
      },
    ],

    credits: {
      enabled: false,
    },

    legend: {
      enabled: false,
    },
  };

  const getPercentValue = (count) => {
    return Math.floor((Number(count) / totalCount) * 100);
  };
  const optionsForValue = {
    chart: {
      type: "pie",
      height: 220,
      backgroundColor: "transparent",
      margin: 0,
    },

    title: {
      text: `<div style="text-align:center;">
                    <span style="font-size:20px;font-weight:400;font-family:Outfit; color:#000000">${totalValue} Cr</span><br/>
                  </div>`,
      align: "center",
      verticalAlign: "middle",
      useHTML: true,
      y: 20,
    },

    tooltip: {
      enabled: true,
      useHTML: true,
      headerFormat: "",
      pointFormat: `
        <div style="font-family: Outfit; font-size: 12px; padding: 4px;">
          <span style="color:{point.color}">●</span> <b>{point.name}</b>: {point.y} Cr
        </div>
      `,
      backgroundColor: "#ffffff",
      borderWidth: 1,
      borderRadius: 8,
      shadow: true,
    },

    plotOptions: {
      pie: {
        innerSize: "60%",
        size: "75%",
        borderWidth: 0,
        borderRadius: 0,
        borderColor: "#fff",
        dataLabels: {
          enabled: true,
          useHTML: true,
          distance: 4,
          connectorPadding: 1,
          crop: false,
          overflow: "allow",
          formatter: function () {
            return `
          <div style="
            max-width: 60px;
            white-space: normal;
            word-wrap: break-word;
            text-align: center;
            font-size: 12px;
            font-weight: 600;
            font-family: Outfit, sans-serif;
          ">
            ${this.point.name} (${this.point.y} Cr)
          </div>
        `;
          },
        },
      },
    },

    series: [
      {
        name: "Change Order Value",
        data: data?.data?.statusByValue.map((item, ind) => ({
          y: Number(item.value),
          name: item.name,
          color: colorPlate[ind],
        })),
      },
    ],

    credits: {
      enabled: false,
    },

    legend: {
      enabled: false,
    },
  };
  return (
    <Box>
      <DashboardPanelHeader eyebrow="Change control" title="Change Order Status" subtitle="See change-order volume and value across each commercial review stage." />
      <Box
        sx={{
          width: "100%",
          borderBottom: "1px solid #E4E4E4",
          p: 1.5,
          display: "none",
          justifyContent: "space-between",
          boxSizing: "border-box",
        }}
      >
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "Outfit, sans-serif",
              fontWeight: 600,
              fontSize: "20px",
              letterSpacing: "0px",
              mb: 0,
            }}
          >
            Change Order Count by Status
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Outfit, sans-serif",
              fontWeight: 300,
              fontSize: "14px",
              letterSpacing: "0px",
              color: "#7D7F81",
            }}
          >
            Overview of Change Order Count categorised by their current stage
          </Typography>
        </Box>
      </Box>
      <Grid
        container
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 2,
        }}
        spacing={3}
      >
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            borderRight: { md: "1px solid #E4E4E4" },
            pr: { md: 4 },
          }}
        >
          <Typography sx={{ fontFamily: "Outfit", fontWeight: 500, mb: 1 }}>
            Status By Value
          </Typography>
          {data?.data?.statusByCount.length > 0 ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={optionsForValue}
            />
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                mt: 13,
                color: "#9e9e9e",
                fontFamily: "Outfit",
              }}
            >
              No data available
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography sx={{ fontFamily: "Outfit", fontWeight: 500, mb: 1 }}>
            Status By Count
          </Typography>
          {data?.data?.statusByCount.length > 0 ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={optionsForCount}
            />
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                mt: 13,
                color: "#9e9e9e",
                fontFamily: "Outfit",
              }}
            >
              No data available
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChangeOrderStatus;
